import { Box, CircularProgress, Modal, Stack, styled, Typography } from "@mui/material";
import { FCC, TOption } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { background, black, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Button } from "@/components/elements/Button";
import { DropZone } from "@/components/elements/DropZone";
import { TExtendFile } from "@/types/file";
import { useState } from "react";
import { downloadPartnerSample, validatePartnerFileFormat } from "@/features/partner/utils/excel";
import * as XLSX from "xlsx";
import { TPartnerRowExcel } from "@/features/partner/types/partner-import";
import { validateImportPartnerApi } from "@/api/services/main/partner";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IconClose, IconCompanyListImport } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { sanitizeInput } from "@/utils/pieces";
import { PARTNER_IMPORT_MAX_ROWS, PARTNER_IMPORT_PARSE_YIELD_INTERVAL } from "@/features/partner/constants/import";

type TImportPartnerModalProps = {
  partnerModal: UseVisibleReturn;
  isLoading?: boolean;
  nationalityOptions: TOption[];
  isLoadingNationality?: boolean;
  contactMethodOptions?: TOption[];
};

type TErrorItem = {
  message: string;
};

export const ImportPartnerModal: FCC<TImportPartnerModalProps> = ({ partnerModal, isLoading, nationalityOptions, isLoadingNationality = false, contactMethodOptions = [] }) => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<File | null>();
  const [errorList, setErrorList] = useState<string[]>([]);
  const navigate = useNavigate();
  const [openFileDialog, setOpenFileDialog] = useState<() => void>();

  const onClose = () => {
    setIsSubmit(false);
    setFile(null);
    setErrorList([]);
    partnerModal.close();
  };

  const handleErrorImportCsv = (errorContainer: unknown) => {
    if (typeof errorContainer === "object" && errorContainer !== null) {
      if ("error" in errorContainer) {
        const listErrors = (errorContainer as { error: unknown }).error;
        if (Array.isArray(listErrors)) {
          setErrorList(listErrors.map((item: TErrorItem) => item.message));
        }
      } else if ("message" in errorContainer) {
        setErrorList([(errorContainer as { message: unknown }).message as string]);
      }
    } else {
      setErrorList(["Unknown error"]);
    }
  };

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    if (!files[0]) {
      setFile(null);
      return;
    }
    setFile(files[0]);
    setErrorList([]);
  };

  const readFileImport = async (file: File): Promise<[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const ext = file.name.split(".").pop()?.toLowerCase();
          let workbook: XLSX.WorkBook;

          if (ext === "csv") {
            workbook = XLSX.read(data as string, {
              type: "string",
              raw: true,
            });
          } else if (ext === "xlsx" || ext === "xlsm" || ext === "xls") {
            workbook = XLSX.read(data, {
              type: "array",
            });
          } else {
            throw new Error("Unsupported file type");
          }

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows: [][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: true,
          });
          validatePartnerFileFormat(rows[0]);
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };

      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "csv") reader.readAsText(file, "utf-8");
      else reader.readAsArrayBuffer(file);
    });
  };

  const handleImportFile = async () => {
    if (!file) return;
    try {
      setIsSubmit(true);

      const rows: [][] = await readFileImport(file);

      const totalRows = rows.length;
      const results: TPartnerRowExcel[] = [];

      for (let i = 1; i < totalRows; i++) {
        const row: string[] = rows[i];
        if (row.length === 0) {
          continue;
        }

        if (row.every((cell) => String(cell).trim() === "")) {
          continue;
        }
        const resultRow = row.map((item: string | number) => (typeof item === "string" ? sanitizeInput(item) : (item ?? "")));
        results.push(resultRow as unknown as TPartnerRowExcel);

        if (results.length > PARTNER_IMPORT_MAX_ROWS) {
          throw new Error(`一度にインポートできるのは最大${PARTNER_IMPORT_MAX_ROWS}件までです。`);
        }

        // Yield periodically so the spinner keeps painting, without paying a macrotask per row.
        if (results.length % PARTNER_IMPORT_PARSE_YIELD_INTERVAL === 0) {
          await new Promise((r) => setTimeout(r, 0));
        }
      }
      const { data: partner } = await validateImportPartnerApi(results);
      partnerModal.close();
      navigate(AppRoutes.partnerImport, { state: { partner } });
    } catch (err) {
      setIsSubmit(false);
      handleErrorImportCsv(err);
    }
  };

  return (
    <Modal
      open={partnerModal.visible}
      hideBackdrop={false}
      sx={{
        position: "fixed",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer width={920}>
        <Stack
          bgcolor={background.disable}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height="53px"
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            padding: "16px",
          }}
        >
          <Stack direction="row" gap={2}>
            <Typography variant="body14Light" color={text.primary}>
              パートナー情報
            </Typography>
            |
            <Typography variant="body14Light" color={text.primary}>
              一括インポート
            </Typography>
          </Stack>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Stack
          sx={{
            padding: "32px 16px ",
            gap: "24px",
          }}
        >
          {!isSubmit && (
            <Box borderColor={divider.middle} borderRadius={rounded.sm}>
              <DropZone
                options={{ multiple: false, noClick: true }}
                onChange={handleChangeFiles}
                rules={{
                  maxFile: 1,
                  acceptMimeTypes: [
                    "text/csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel.sheet.macroEnabled.12",
                  ],
                }}
                isShowErr={false}
                triggerOpenChosenFile={setOpenFileDialog}
              >
                <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
                  アップロードするファイルをドラッグ&ドロップ
                </Typography>
                <Typography variant="body14Light" color={black[90]} whiteSpace="pre-wrap" textAlign="center">
                  （ファイル形式：csv、xlsx、xlsmに対応）
                </Typography>
                <Typography variant="body14Light" width={920} textAlign="center">
                  または
                </Typography>
                <Button type="button" variant="outline" onClick={() => openFileDialog?.()}>
                  ファイルを選択する
                </Button>
                <Typography variant="cap12Light" color={black[70]} textAlign="start">
                  <ul>
                    <li>データ内に改行は含めないでください。</li>
                    <li>容量は最大10MBまでとなります。</li>
                    <li>一度にインポートできるのは最大{PARTNER_IMPORT_MAX_ROWS}件までです。</li>
                  </ul>
                </Typography>
              </DropZone>
              {errorList.length > 0 && (
                <Stack
                  sx={{ mt: "32px", p: "8px", background: background.error, color: text.error, maxHeight: "160px", overflowY: "auto" }}
                  borderRadius={rounded.xs}
                >
                  {errorList.map((item, index) => (
                    <Typography key={index} component="p" variant="cap12">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {isSubmit && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "150px" }}>
              <Box sx={{ height: 60 }}>
                <CircularProgress />
              </Box>
              <Typography variant="body14Light" textAlign="center">
                処理中
              </Typography>
            </Box>
          )}
        </Stack>
        <hr />
        <Stack
          flexDirection="row"
          gap="16px"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: "16px 32px",
          }}
        >
          <Stack flexDirection="row" gap="16px" justifyContent="flex-start">
            <Button variant="ghost" onClick={onClose}>
              キャンセル
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-end">
            {!isSubmit && (
              <Button
                variant="outline"
                disabled={isLoadingNationality}
                onClick={() => downloadPartnerSample(nationalityOptions, contactMethodOptions)}
                startIcon={<FileDownloadIcon />}
              >
                テンプレートをダウンロード
              </Button>
            )}
            <Button
              isLoading={isLoading}
              disabled={isSubmit || !file || errorList.length > 0}
              onClick={handleImportFile}
              startIcon={<IconCompanyListImport />}
            >
              {isSubmit ? "ファイルを処理中..." : "インポート"}
            </Button>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  min-width: 417px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none;

  &:focus {
    outline: none;
  }
`;
